import type { IconType } from "react-icons";
import {
    FaChartBar,
    FaClock,
    FaFileImport,
    FaFilter,
    FaImage,
    FaLayerGroup,
    FaPaperPlane,
    FaPhone,
    FaSms,
} from "react-icons/fa";
import contentCalendarDashboard from "../assets/software/social-media-automation/content-calendar.svg";
import smartSchedulingDashboard from "../assets/software/social-media-automation/smart-scheduling.svg";
import engagementInboxDashboard from "../assets/software/social-media-automation/engagement-inbox.svg";

/* ── WhatsApp Bulk Sender ── */

export const wbsKpis = [
    { label: "Messages Sent Today", value: 24800, suffix: "+" },
    { label: "Delivery Rate", value: 98, suffix: "%" },
    { label: "Active Campaigns", value: 36, suffix: "" },
    { label: "Campaign ROI", value: 4.8, suffix: "x", decimals: 1 },
];

export const wbsModules: { title: string; desc: string; icon: IconType }[] = [
    { title: "Contact Import", desc: "Upload CSV or sync CRM lists with duplicate detection.", icon: FaFileImport },
    { title: "Audience Segments", desc: "Filter by tags, purchase history, or custom fields.", icon: FaFilter },
    { title: "Template Manager", desc: "Approved WhatsApp templates with variable placeholders.", icon: FaLayerGroup },
    { title: "Media Attachments", desc: "Send images, PDFs, and catalog cards at scale.", icon: FaImage },
    { title: "Schedule & Queue", desc: "Time-zone aware delivery windows and throttling.", icon: FaClock },
    { title: "Delivery Reports", desc: "Read receipts, failures, and opt-out tracking.", icon: FaChartBar },
];

export const wbsInsightMetrics = [
    {
        title: "Delivery Success",
        value: 98,
        suffix: "%",
        desc: "Messages successfully delivered",
        tone: "green" as const,
    },
    {
        title: "Audience Engagement",
        value: 72,
        suffix: "%",
        desc: "Opened and interacted messages",
        tone: "purple" as const,
    },
    {
        title: "Responses Received",
        value: 4.2,
        suffix: "K",
        desc: "Customer replies tracked",
        tone: "violet" as const,
        decimals: 1,
    },
    {
        title: "Active Contact List",
        value: 18.5,
        suffix: "K",
        desc: "Reachable opted-in contacts",
        tone: "cyan" as const,
        decimals: 1,
    },
];

/* ── Social Media Automation ── */

export const smaProblems = [
    "Posts go live inconsistently across channels",
    "Teams juggle multiple logins and calendars",
    "Engagement drops when publishing slows down",
    "No single view of what performed best",
];

export const smaSolutions = [
    "Unified calendar for every major platform",
    "Auto-publish at peak audience times",
    "AI-assisted caption and hashtag suggestions",
    "Cross-channel performance dashboard",
];

export const smaFeatures = [
    {
        title: "Content Calendar",
        desc: "Drag-and-drop planning with channel previews for Instagram, Facebook, LinkedIn, and X.",
        side: "left" as const,
        image: contentCalendarDashboard,
        alt: "Social media content calendar with monthly scheduling, platform post cards, and draft, scheduled, and published labels",
    },
    {
        title: "Smart Scheduling",
        desc: "Publish when your audience is most active — per platform, per timezone.",
        side: "right" as const,
        image: smartSchedulingDashboard,
        alt: "Smart scheduling dashboard with posting queues, best-time suggestions, and audience activity heatmap",
    },
    {
        title: "Engagement Inbox",
        desc: "Reply to comments and DMs from one queue without switching tabs.",
        side: "left" as const,
        image: engagementInboxDashboard,
        alt: "Unified engagement inbox showing Instagram comments, Facebook messages, LinkedIn replies, and X mentions with reply composer",
    },
];

export const smaMetrics = [
    { label: "Publishing Consistency", value: 94, color: "#9e00ff" },
    { label: "Time Saved Weekly", value: 78, color: "#002454" },
    { label: "Engagement Lift", value: 62, color: "#7a00a8" },
];

/* ── WhatsApp API Platform ── */

export const wapIndustries = [
    {
        id: "ecommerce",
        label: "E-commerce",
        headline: "Order updates that customers actually read",
        description:
            "Turn WhatsApp into your highest-converting retention channel with real-time order, catalog, and cart recovery flows.",
        points: [
            "Abandoned cart recovery",
            "Shipping and delivery alerts",
            "Product catalog messages",
            "Post-purchase review requests",
        ],
    },
    {
        id: "healthcare",
        label: "Healthcare",
        headline: "Appointment reminders patients trust",
        description:
            "Send HIPAA-aware patient communications that reduce no-shows and keep care journeys on track.",
        points: [
            "Booking confirmations",
            "Prescription ready alerts",
            "Secure follow-up care",
            "Lab report notifications",
        ],
    },
    {
        id: "finance",
        label: "Finance",
        headline: "Secure transactional messaging",
        description:
            "Deliver time-sensitive financial updates with verified templates, OTP flows, and audit-ready delivery logs.",
        points: [
            "OTP and verification flows",
            "Payment due reminders",
            "Account activity alerts",
            "Loan and EMI notifications",
        ],
    },
    {
        id: "education",
        label: "Education",
        headline: "Student communication at scale",
        description:
            "Keep students, parents, and faculty aligned with automated academic updates across the institution.",
        points: [
            "Class and exam schedules",
            "Fee payment reminders",
            "Admission status updates",
            "Results and attendance alerts",
        ],
    },
    {
        id: "real-estate",
        label: "Real Estate",
        headline: "Property updates that close deals faster",
        description:
            "Nurture buyers and tenants with instant listing alerts, visit confirmations, and offer-stage updates.",
        points: [
            "New listing notifications",
            "Site visit scheduling",
            "Offer and negotiation updates",
            "Document collection reminders",
        ],
    },
    {
        id: "travel",
        label: "Travel & Hospitality",
        headline: "Guest experiences that start before check-in",
        description:
            "Automate booking confirmations, itinerary updates, and on-trip support for hotels and travel brands.",
        points: [
            "Booking confirmations",
            "Check-in and digital keys",
            "Itinerary and gate updates",
            "Concierge and feedback requests",
        ],
    },
    {
        id: "logistics",
        label: "Logistics",
        headline: "Delivery visibility customers expect",
        description:
            "Give recipients live shipment status, driver coordination, and proof-of-delivery updates on WhatsApp.",
        points: [
            "Real-time tracking updates",
            "Out-for-delivery alerts",
            "Driver contact handoff",
            "Proof of delivery confirmation",
        ],
    },
];

export const wapIntegrations = [
    { name: "Salesforce", abbr: "SF" },
    { name: "HubSpot", abbr: "HS" },
    { name: "Zoho", abbr: "ZO" },
    { name: "Shopify", abbr: "SH" },
    { name: "Slack", abbr: "SL" },
    { name: "Google Sheets", abbr: "GS" },
];

/* ── SMS / Call / Email Broadcast ── */

export const scebChannels = [
    { name: "SMS", icon: FaSms, stat: "2.4M", label: "Texts delivered / mo" },
    { name: "Voice", icon: FaPhone, stat: "180K", label: "Calls completed / mo" },
    { name: "Email", icon: FaPaperPlane, stat: "890K", label: "Emails sent / mo" },
];

export const scebWhyChoose = [
    {
        id: "builder",
        title: "Unified Campaign Builder",
        desc: "Create SMS, Email, and Voice campaigns from one dashboard.",
        icon: FaLayerGroup,
    },
    {
        id: "segmentation",
        title: "Smart Audience Segmentation",
        desc: "Target the right users based on behavior, location, or custom filters.",
        icon: FaFilter,
    },
    {
        id: "scheduling",
        title: "Automated Scheduling",
        desc: "Schedule campaigns across different channels and time zones.",
        icon: FaClock,
    },
    {
        id: "analytics",
        title: "Real-Time Analytics",
        desc: "Track delivery, opens, clicks, and call performance instantly.",
        icon: FaChartBar,
    },
];

export const scebReachStats = [
    { channel: "SMS", rate: 96 },
    { channel: "Voice", rate: 72 },
    { channel: "Email", rate: 88 },
];
